import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal } from "../site/motion";
import { CLINIC } from "../site/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = "w-full bg-transparent border-0 border-b border-primary/25 focus:border-secondary outline-none py-4 text-foreground placeholder:text-muted-foreground/70 transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. Our care team will reach out to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-background py-28 md:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-14">
        <Reveal className="md:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-secondary mb-6">Reach Out</p>
          <h2 className="font-serif font-light text-foreground leading-[0.95] text-5xl md:text-7xl">Let&apos;s talk.<br />In confidence.</h2>
          <p className="text-muted-foreground text-lg leading-relaxed mt-8 max-w-md">
            Taking the first step is the hardest part. Send us a message and a member of our nursing team will respond privately — no judgement, no pressure.
          </p>
          <div className="mt-10 space-y-3 text-sm">
            <p data-testid="contact-phone" className="text-foreground"><span className="text-muted-foreground uppercase tracking-[0.14em] text-xs mr-3">Phone</span>{CLINIC.phone}</p>
            <p data-testid="contact-email" className="text-foreground"><span className="text-muted-foreground uppercase tracking-[0.14em] text-xs mr-3">Email</span>{CLINIC.email}</p>
          </div>
        </Reveal>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="md:col-span-6 md:col-start-7 space-y-8"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <input data-testid="contact-name" className={field} placeholder="Full name *" value={form.name} onChange={set("name")} />
            <input data-testid="contact-email-input" type="email" className={field} placeholder="Email address *" value={form.email} onChange={set("email")} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <input data-testid="contact-phone-input" className={field} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
            <input data-testid="contact-subject" className={field} placeholder="Subject (optional)" value={form.subject} onChange={set("subject")} />
          </div>
          <textarea data-testid="contact-message" rows={5} className={field + " resize-none"} placeholder="How can we help? *" value={form.message} onChange={set("message")} />
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto bg-primary text-primary-foreground px-12 py-5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-secondary transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
